import { useCallback, useMemo } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { makeStyles } from '@material-ui/core';
import VirtualizedList from '../virtualizedList';
import CardsTableHeader from './header';
import CardsTableRow from './row';

const ROW_HEIGHT = 68;
const ROW_GAP = 16;

const useStyles = makeStyles(({ breakpoints }) => ({
  table: {
    display: 'grid',
    width: '100%',
  },
  scrollY: {
    right: -11,
    [breakpoints.up('xl')]: {
      right: -18,
    },
  },
}));

const CardsTable = ({
  className = null,
  customHeaderClass = null,
  headers,
  rows = [],
  preHeaderActions = [],
  headerActions = [],
  prepareDetailsUrl,
  hasDetailsUrlCheck = () => true,
  hasNextPage = false,
  isNextPageLoading = false,
  onLoadMore = () => {},
  scrollElementRef = undefined,
  rowGap = ROW_GAP,
}) => {
  const classes = useStyles();
  const headerItems = useMemo(
    () => [...preHeaderActions, ...headers, ...headerActions],
    [preHeaderActions, headers, headerActions]
  );

  const prepareRowSize = useCallback(() => ROW_HEIGHT, []);

  const renderRow = useCallback(
    row => (
      <CardsTableRow
        headers={headerItems}
        row={row}
        hasDetailsUrl={hasDetailsUrlCheck(row)}
        prepareDetailsUrl={prepareDetailsUrl}
      />
    ),
    [headerItems, hasDetailsUrlCheck, prepareDetailsUrl]
  );

  return (
    <div className={classNames(classes.table, className)}>
      <CardsTableHeader
        customHeaderClass={customHeaderClass}
        headers={headerItems}
      />
      <VirtualizedList
        scrollElementRef={scrollElementRef}
        verticalScrollClass={classes.scrollY}
        items={rows}
        count={rows?.length ?? 0}
        gap={rowGap ?? ROW_GAP}
        estimateSize={prepareRowSize}
        hasNextPage={hasNextPage}
        isNextPageLoading={isNextPageLoading}
        renderItem={renderRow}
        onLoadMore={onLoadMore}
        shouldUseWindowScroll
      />
    </div>
  );
};

CardsTable.propTypes = {
  className: PropTypes.string,
  customHeaderClass: PropTypes.string,
  headers: PropTypes.arrayOf(PropTypes.shape({})).isRequired,
  rows: PropTypes.arrayOf(PropTypes.object),
  preHeaderActions: PropTypes.arrayOf(PropTypes.shape({})),
  headerActions: PropTypes.arrayOf(PropTypes.shape({})),
  hasNextPage: PropTypes.bool,
  isNextPageLoading: PropTypes.bool,
  prepareDetailsUrl: PropTypes.func.isRequired,
  hasDetailsUrlCheck: PropTypes.func,
  onLoadMore: PropTypes.func,
  scrollElementRef: PropTypes.shape({ current: PropTypes.instanceOf(Element) }),
  rowGap: PropTypes.number,
};

export default CardsTable;
